/**
 * WHERE DOES "ADD JOB" LIVE?
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS IS A FUNCTION AND NOT TWO `&&`s
 * ═══════════════════════════════════════════════════════════════════════════
 * Two independent exposure flags used to decide this between them, from
 * opposite sides of the app, and neither could see the whole answer:
 *
 *   `ui_partsAndLabor`     re-homes the Jobs section OUT of the Details tab.
 *                          `TicketDetailsPopup` hid Jobs when it was ON.
 *   `ui_ticketPartsPanel`  renders the Parts tab Jobs were re-homed INTO.
 *                          `TicketPartsPanel` is what holds `JobsSection`.
 *
 * Nothing couples them. `getExposureFlags` is a flat spread — no dependency
 * map, no validation — and both default OFF, so an admin can turn on the
 * re-home without turning on its destination.
 *
 * At Floyd's that is what happened. `ui_partsAndLabor ON` with
 * `ui_ticketPartsPanel OFF` hid the Jobs section in favour of a tab that was
 * not rendered, and an advisor who had just accepted a ticket into the
 * Command Center had no way to add a job to it.
 *
 * The bug was not in either component. It was that a single product question
 * — where is the one place you add a job — was answered in two halves. So it
 * is answered once, here, and both surfaces ask.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE RULE
 * ═══════════════════════════════════════════════════════════════════════════
 * Jobs live in the Parts & Labor tab ONLY when that tab can actually be
 * reached. Every other combination keeps them in Details, which is the
 * legacy home and is always rendered.
 *
 * This is deliberately a PLACEMENT decision, never an authorization one.
 * Both surfaces open the same `AddJobDialog`, which is the scheduler-safe
 * write path and where permission is enforced. A flag must never decide
 * whether somebody may add a job — only where the button sits.
 */

/** The one surface that owns Add Job for a given flag combination. */
export type AddJobSurface = 'details' | 'parts';

export interface AddJobSurfaceFlags {
  /** `ui_partsAndLabor` — re-homes Jobs out of the Details tab. */
  partsAndLaborEnabled: boolean;
  /** `ui_ticketPartsPanel` — renders the Parts tab that receives them. */
  ticketPartsPanelEnabled: boolean;
}

/**
 * Exactly one surface, for every supported flag combination.
 *
 * | partsAndLabor | ticketPartsPanel | surface   |
 * |---------------|------------------|-----------|
 * | OFF           | OFF              | details   |
 * | OFF           | ON               | details   |
 * | ON            | OFF              | details   |  ← the Floyd case
 * | ON            | ON               | parts     |
 */
export function resolveAddJobSurface(flags: AddJobSurfaceFlags): AddJobSurface {
  return flags.partsAndLaborEnabled && flags.ticketPartsPanelEnabled ? 'parts' : 'details';
}
